/**
 * Add/Edit Facility Modal
 * Single responsibility: present form and delegate validation/submit to useFacilityForm.
 * Dependency Inversion: receives onSubmit/onUpdate/onClose from parent.
 */

import React, { useState, useCallback } from 'react';
import { Input, Label } from '../common';
import { X, Trash2 } from 'lucide-react';
import { useFacilityForm } from './hooks/useFacilityForm';

export function AddFacilityModal({
  isOpen,
  onClose,
  onSubmit,
  onUpdate,
  onDeleteClick,
  organizationId,
  facilityToEdit,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateAll,
    isFormValid,
    hasChanges,
    resetForm,
    getSubmitPayload,
  } = useFacilityForm(facilityToEdit, isOpen);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!validateAll()) return;

      setIsSubmitting(true);
      try {
        const payload = getSubmitPayload();
        if (facilityToEdit && onUpdate) {
          await onUpdate(facilityToEdit.id, payload);
        } else {
          await onSubmit(payload);
        }
        resetForm();
        onClose();
      } catch (error) {
        // Parent shows error toast
      } finally {
        setIsSubmitting(false);
      }
    },
    [validateAll, getSubmitPayload, facilityToEdit, onUpdate, onSubmit, resetForm, onClose],
  );

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleDeleteClick = useCallback(() => {
    if (facilityToEdit && onDeleteClick) {
      onDeleteClick(facilityToEdit);
      handleCancel();
    }
  }, [facilityToEdit, onDeleteClick, handleCancel]);

  if (!isOpen) return null;

  const modalTitle = facilityToEdit ? 'Edit Facility' : 'Add New Facility';

  return (
    <>
      <div
        className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-40"
        onClick={handleCancel}
        role="presentation"
      />

      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-6 sm:px-11 py-5 sm:py-7 w-[90vw] max-w-[734px] max-h-[90vh] overflow-y-auto z-50">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-6">
          <div className="flex items-center justify-between">
            <h2
              className="font-['Roboto:Bold',sans-serif] font-bold text-2xl sm:text-[28px] text-black tracking-[0.14px]"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              {modalTitle}
            </h2>
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center justify-center w-[45px] h-[45px] rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          <div className="flex flex-col gap-1.5 sm:gap-1">
            <FormField
              id="name"
              label="Facility Name"
              placeholder="e.g., Memorial Hospital East"
              value={formData.name}
              error={errors.name}
              touched={touched.name}
              onChange={handleInputChange('name')}
              onBlur={() => handleBlur('name')}
              fullWidth
            />
            <FormField
              id="street"
              label="Street"
              placeholder="e.g., 123 Oak Avenue"
              value={formData.street}
              error={errors.street}
              touched={touched.street}
              onChange={handleInputChange('street')}
              onBlur={() => handleBlur('street')}
              fullWidth
            />
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_0.8fr] gap-2 sm:gap-4 w-full">
              <FormField
                id="city"
                label="City"
                placeholder="e.g., Dallas"
                value={formData.city}
                error={errors.city}
                touched={touched.city}
                onChange={handleInputChange('city')}
                onBlur={() => handleBlur('city')}
              />
              <FormField
                id="state"
                label="State"
                placeholder="e.g., TX"
                value={formData.state}
                error={errors.state}
                touched={touched.state}
                onChange={handleInputChange('state')}
                onBlur={() => handleBlur('state')}
                maxLength={2}
              />
              <FormField
                id="zipCode"
                label="Zip Code"
                placeholder="e.g., 75201"
                value={formData.zipCode}
                error={errors.zipCode}
                touched={touched.zipCode}
                onChange={handleInputChange('zipCode')}
                onBlur={() => handleBlur('zipCode')}
                maxLength={10}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 sm:gap-11 justify-center">
            <button
              type="button"
              onClick={handleCancel}
              className="border-2 border-[#61b8e7] bg-white text-black rounded-[10px] px-8 py-2.5 font-['Roboto:Bold',sans-serif] font-bold text-base min-w-[200px] sm:min-w-[253px] hover:bg-[#61b8e7]/5 transition-colors"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !isFormValid() || (facilityToEdit && !hasChanges)}
              className="bg-[#61b8e7] text-white rounded-[10px] px-8 py-2.5 font-['Roboto:Bold',sans-serif] font-bold text-base min-w-[200px] sm:min-w-[253px] hover:bg-[#4da3d1] disabled:bg-[rgba(97,184,231,0.4)] disabled:cursor-not-allowed transition-colors"
              style={{ fontVariationSettings: "'wdth' 100" }}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </button>
          </div>

          {facilityToEdit && onDeleteClick && (
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={handleDeleteClick}
                className="flex items-center gap-1.5 py-1 text-gray-500 hover:text-red-600 transition-colors group"
                aria-label="Delete facility"
              >
                <Trash2 className="w-4 h-4 shrink-0 group-hover:text-red-600 transition-colors" />
                <span className="font-['Roboto:Regular',sans-serif] text-[13px] underline-offset-2 group-hover:underline">
                  Delete Facility
                </span>
              </button>
            </div>
          )}
        </form>
      </div>
    </>
  );
}

function FormField({
  id,
  label,
  placeholder,
  value,
  error,
  touched,
  onChange,
  onBlur,
  fullWidth = false,
  maxLength,
}) {
  const inputClass = fullWidth
    ? `w-full !max-w-full ${error ? 'border-red-500' : ''}`
    : error
      ? 'border-red-500'
      : '';

  return (
    <div className={fullWidth ? 'form-group w-full' : 'form-group'}>
      <Label htmlFor={id} variant="gray" isRequired>
        {label}
      </Label>
      <Input
        id={id}
        placeholder={placeholder}
        customClass={inputClass}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        maxLength={maxLength}
      />
      <div className="min-h-[1.25rem] mt-1" aria-live="polite">
        {error && touched && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    </div>
  );
}
